// Shared utilities for matching FE breakdown rows to portfolio funds
// and resolving FE columns into CBAM Master Sector contributions.

export interface FeBreakdownRow {
  id: string;
  isin: string;
  fund_name: string;
  as_of_date: string;
  breakdown: Record<string, number>;
}

export interface FeMappingRow {
  fe_column_name: string;
  cbam_master_sector: string | null;
  mapping_type: "DIRECT" | "SPLIT" | "EXPANDED";
  children: string[];
  display_order: number;
}

export interface FeSplitOverride {
  isin: string;
  fe_column_name: string;
  child_sector: string;
  pct: number;
}

// Normalize fund name for fuzzy matching (lowercase, strip share-class noise,
// remove punctuation, collapse whitespace).
export function normalizeFundName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\bin gb\b/g, "")
    .replace(/\btr in gb\b/g, "")
    .replace(/\btotal return\b/g, "")
    .replace(/\b(acc|inc|dist|gbp|usd|eur)\b/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(name: string): Set<string> {
  return new Set(normalizeFundName(name).split(" ").filter((t) => t.length > 1));
}

// Jaccard similarity on word sets.
export function nameSimilarity(a: string, b: string): number {
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let intersection = 0;
  for (const t of ta) if (tb.has(t)) intersection++;
  return intersection / (ta.size + tb.size - intersection);
}

export interface PortfolioFund {
  id: string;
  fund_name: string;
  isin: string | null;
  fund_manager: string | null;
  fund_class: string | null;
}

export interface FundMatch {
  fund: PortfolioFund;
  fe: FeBreakdownRow | null;
  matchType: "isin" | "fuzzy" | "none";
  confidence: number;
}

const FUZZY_THRESHOLD = 0.6;

export function matchFunds(
  portfolioFunds: PortfolioFund[],
  feRows: FeBreakdownRow[],
): FundMatch[] {
  // Index FE rows by ISIN (latest only — feRows should already be deduped).
  const byIsin = new Map<string, FeBreakdownRow>();
  for (const r of feRows) {
    if (r.isin) byIsin.set(r.isin.toUpperCase().trim(), r);
  }

  return portfolioFunds.map((fund) => {
    const isin = (fund.isin ?? "").toUpperCase().trim();
    if (isin) {
      // ISIN is authoritative: match by ISIN only, no fuzzy fallback.
      // This prevents mis-pairing funds with similar names but different ISINs.
      if (byIsin.has(isin)) {
        return { fund, fe: byIsin.get(isin)!, matchType: "isin", confidence: 1 };
      }
      return { fund, fe: null, matchType: "none", confidence: 0 };
    }
    // Only funds without an ISIN on file fall back to fuzzy name matching.
    let best: { row: FeBreakdownRow; score: number } | null = null;
    for (const row of feRows) {
      const score = nameSimilarity(fund.fund_name, row.fund_name);
      if (!best || score > best.score) best = { row, score };
    }
    if (best && best.score >= FUZZY_THRESHOLD) {
      return { fund, fe: best.row, matchType: "fuzzy", confidence: best.score };
    }
    return { fund, fe: null, matchType: "none", confidence: 0 };
  });
}

// Compute the CBAM Master Sector allocation for a single fund based on its
// FE breakdown, the mapping table, and any per-fund split overrides.
// Returns { cbamSectorName: pct } summing to ~100 (minus any unresolved residual).
export interface FundCbamResult {
  allocation: Record<string, number>;
  unresolved: { feColumn: string; pct: number; parent: string; children: string[] }[];
  total: number;
}

export function resolveFundCbam(
  fe: FeBreakdownRow,
  mappingByCol: Map<string, FeMappingRow>,
  overridesByIsinCol: Map<string, FeSplitOverride[]>,
  priorAllocation?: Record<string, number>,
): FundCbamResult {
  const allocation: Record<string, number> = {};
  const unresolved: FundCbamResult["unresolved"] = [];
  let total = 0;

  for (const [feCol, raw] of Object.entries(fe.breakdown)) {
    const pct = Number(raw) || 0;
    if (pct <= 0) continue;
    total += pct;
    // FE breakdown keys are sometimes stored bare ("North American Equities")
    // while fe_sector_mapping rows use the prefixed form ("Asset % North
    // American Equities"). Try both so DIRECT mappings always resolve.
    const mapping =
      mappingByCol.get(feCol) ??
      mappingByCol.get(`Asset % ${feCol}`) ??
      mappingByCol.get(feCol.replace(/^Asset %\s*/, ""));
    if (!mapping) {
      // Unmapped FE column — surface as unresolved so the user is forced to
      // split it across CBAM sectors via the Set split dialog.
      unresolved.push({
        feColumn: feCol,
        pct,
        parent: feCol.replace(/^Asset %\s*/, ""),
        children: [],
      });
      const parentLabel = `[needs split] ${feCol.replace(/^Asset %\s*/, "")}`;
      allocation[parentLabel] = (allocation[parentLabel] ?? 0) + pct;
      continue;
    }
    if (mapping.mapping_type === "DIRECT" && mapping.cbam_master_sector) {
      allocation[mapping.cbam_master_sector] =
        (allocation[mapping.cbam_master_sector] ?? 0) + pct;
      continue;
    }
    // SPLIT / EXPANDED — look for overrides on this ISIN+column.
    const key = `${fe.isin}::${feCol}`;
    const overrides = overridesByIsinCol.get(key) ?? [];
    const overrideTotal = overrides.reduce((s, o) => s + Number(o.pct), 0);
    if (overrides.length > 0 && Math.abs(overrideTotal - 100) < 0.5) {
      for (const o of overrides) {
        const child = o.child_sector;
        allocation[child] = (allocation[child] ?? 0) + (pct * Number(o.pct)) / 100;
      }
      continue;
    }
    // Fall back to previous split inferred from the fund's prior CBAM mapping:
    // use the ratio of prior weights across this column's children.
    if (priorAllocation && mapping.children.length > 0) {
      const priorChildren = mapping.children
        .map((c) => ({ name: c, w: Number(priorAllocation[c] ?? 0) }))
        .filter((x) => x.w > 0);
      const priorSum = priorChildren.reduce((s, x) => s + x.w, 0);
      if (priorSum > 0) {
        for (const { name, w } of priorChildren) {
          allocation[name] = (allocation[name] ?? 0) + (pct * w) / priorSum;
        }
        continue;
      }
    }
    unresolved.push({
      feColumn: feCol,
      pct,
      parent: feCol.replace(/^Asset %\s*/, ""),
      children: mapping.children,
    });
    const parentLabel = `${mapping.children.length ? "[needs split] " : ""}${feCol.replace(/^Asset %\s*/, "")}`;
    allocation[parentLabel] = (allocation[parentLabel] ?? 0) + pct;
  }

  return { allocation, unresolved, total };
}
